import ActionTypes from '../constants/ActionTypes';

const initialState = {
    isFetching: {},
    facilities: [],
    cameras: [],
    gauges: [],
    statuses: [],
    imageListData: [],
    gaugeData: {},
    failure_id: null,
    searchCount: null,
    searchKeys: [],
    new_start_date: null,
    selectedItem: null,
    cameraInfo: null,
    error: null,
    isFetched: false,
    is_null: false,
    relativeActive: null,
};

// Update data register and data edit (based on next_data, no diff_type, no index)
const updateData = (state, updatedItem) => {
    const keyOf = (device_id, gauge_id, get_datetime) => `${device_id}${gauge_id}${get_datetime}`;
    const currentKey = keyOf(updatedItem.device_id, updatedItem.gauge_id, updatedItem.get_datetime);

    // Map current measure_val (to assign to prev_measure_val of the next record)
    const currentMeasureMap = new Map(
        (updatedItem.gauge_data ?? []).map(g => [g.measure_id, g.measure_val ?? null])
    );

    // Map next_gauge_data (diff and flags) by measure_id
    const nextGaugeMap = new Map(
        (updatedItem.next_gauge_data ?? []).map(g => [g.measure_id, g])
    );

    // 1) Update the current record with API.gauge_data (merge by measure_id)
    const updatedGaugeData = {
        ...state.gaugeData,
        [currentKey]: (state.gaugeData[currentKey] || []).map(gauge => {
            const upd = (updatedItem.gauge_data || []).find(x => x.measure_id === gauge.measure_id);
            return upd ? {
                ...gauge,
                ...upd, // keep existing prev_measure_val of the current record (if any)
                diff_val: upd.diff_val ?? gauge.diff_val,
                abnormal_flg: upd.abnormal_flg ?? gauge.abnormal_flg,
                threshold_min_flg: upd.threshold_min_flg ?? gauge.threshold_min_flg,
                threshold_max_flg: upd.threshold_max_flg ?? gauge.threshold_max_flg,
                diff_threshold_min_flg: upd.diff_threshold_min_flg ?? gauge.diff_threshold_min_flg,
                diff_threshold_max_flg: upd.diff_threshold_max_flg ?? gauge.diff_threshold_max_flg,
            } : gauge;
        })
    };

    // 2) Update the NEXT records based on next_data
    const nextList = updatedItem.next_data ?? [];
    let nextMergedGaugeData = { ...updatedGaugeData };

    nextList.forEach(nd => {
        const nextKey = keyOf(nd.device_id ?? updatedItem.device_id, nd.gauge_id, nd.get_datetime);
        const baseArr = state.gaugeData[nextKey] || [];

        nextMergedGaugeData[nextKey] = baseArr.map(gauge => {
            const n = nextGaugeMap.get(gauge.measure_id);
            const prevVal = currentMeasureMap.get(gauge.measure_id) ?? null;

            return {
                ...gauge,
                // prev_measure_val of the next record = measure_val of the current record (same measure_id)
                prev_measure_val: prevVal,
                // merge diff/flags if API provides them
                diff_val: n ? (n.diff_val ?? null) : gauge.diff_val,
                abnormal_flg: n?.abnormal_flg ?? gauge.abnormal_flg,
                diff_threshold_min_flg: n?.diff_threshold_min_flg ?? gauge.diff_threshold_min_flg,
                diff_threshold_max_flg: n?.diff_threshold_max_flg ?? gauge.diff_threshold_max_flg,
            };
        });
    });

    // 3) Update imageListData: current record + status for records in next_data
    const nextStatusKeySet = new Set(
        nextList.map(nd => `${(nd.device_id ?? updatedItem.device_id)}${nd.gauge_id}${nd.get_datetime}`)
    );
    const nextStatusByKey = new Map(
        nextList.map(nd => [
            `${(nd.device_id ?? updatedItem.device_id)}${nd.gauge_id}${nd.get_datetime}`,
            { status_class: nd.status_class, status: nd.status }
        ])
    );

    const updatedImageListData = state.imageListData.map(item => {
        // current record
        if (
            item.device_id === updatedItem.device_id &&
            item.gauge_id === updatedItem.gauge_id &&
            item.get_datetime === updatedItem.get_datetime
        ) {
            return {
                ...item,
                comment: updatedItem.comment,
                comment_font_size: updatedItem.comment_font_size,
                disuse_flg: updatedItem.disuse_flg,
                status_class: updatedItem.status_class,
                user_name: updatedItem.record_user_name,
            };
        }

        // records in next_data
        const k = `${item.device_id}${item.gauge_id}${item.get_datetime}`;
        if (nextStatusKeySet.has(k)) {
            const ns = nextStatusByKey.get(k);
            return { ...item, status_class: ns?.status_class, status: ns?.status };
        }

        return item;
    });

    return {
        ...state,
        imageListData: updatedImageListData,
        gaugeData: nextMergedGaugeData,
    };
};


// Delete data and update next (based on next_data, no index)
const clearData = (state, updatedItem) => {
    const keyOf = (device_id, gauge_id, get_datetime) => `${device_id}${gauge_id}${get_datetime}`;
    const currentKey = keyOf(updatedItem.device_id, updatedItem.gauge_id, updatedItem.get_datetime);

    // Map current measure_val (before delete) to pass into prev_measure_val of the next record
    const currentMeasureMap = new Map(
        (updatedItem.gauge_data ?? []).map(g => [g.measure_id, g.measure_val ?? null])
    );

    // Map next_gauge_data by measure_id (diff for the next record)
    const nextGaugeMap = new Map(
        (updatedItem.next_gauge_data ?? []).map(g => [g.measure_id, g])
    );

    // 1) Clear data in the current record
    const updatedGaugeData = {
        ...state.gaugeData,
        [currentKey]: (state.gaugeData[currentKey] || []).map(gauge => {
            const { measure_val, measureVal, ...rest } = gauge;
            return {
                ...rest,
                diff_val: null,
                abnormal_flg: 0,
                diff_threshold_min_flg: 0,
                diff_threshold_max_flg: 0,
            };
        })
    };

    // 2) Update the NEXT records based on next_data
    const nextList = updatedItem.next_data ?? [];
    let nextMergedGaugeData = { ...updatedGaugeData };

    nextList.forEach(nd => {
        const nextKey = keyOf(nd.device_id ?? updatedItem.device_id, nd.gauge_id, nd.get_datetime);
        const baseArr = state.gaugeData[nextKey] || [];

        nextMergedGaugeData[nextKey] = baseArr.map(gauge => {
            const n = nextGaugeMap.get(gauge.measure_id);
            const prevVal = currentMeasureMap.get(gauge.measure_id) ?? null;

            return n ? {
                ...gauge,
                prev_measure_val: prevVal,
                diff_val: n.diff_val ?? null,
                abnormal_flg: n.abnormal_flg ?? gauge.abnormal_flg,
                diff_threshold_min_flg: n.diff_threshold_min_flg ?? gauge.diff_threshold_min_flg,
                diff_threshold_max_flg: n.diff_threshold_max_flg ?? gauge.diff_threshold_max_flg,
            } : {
                ...gauge,
                prev_measure_val: prevVal,
                diff_val: null,
            };
        });
    });

    // 3) Update imageListData: reset the current record + update status for records in next_data
    const nextStatusKeySet = new Set(
        nextList.map(nd => `${(nd.device_id ?? updatedItem.device_id)}${nd.gauge_id}${nd.get_datetime}`)
    );
    const nextStatusByKey = new Map(
        nextList.map(nd => [
            `${(nd.device_id ?? updatedItem.device_id)}${nd.gauge_id}${nd.get_datetime}`,
            { status_class: nd.status_class, status: nd.status }
        ])
    );

    const updatedImageListData = state.imageListData.map(item => {
        if (
            item.device_id === updatedItem.device_id &&
            item.gauge_id === updatedItem.gauge_id &&
            item.get_datetime === updatedItem.get_datetime
        ) {
            return {
                ...item,
                comment: null,
                comment_font_size: null,
                disuse_flg: 0,
                status_class: 0,
                diff_threshold_min_flg: 0,
                diff_threshold_max_flg: 0,
                user_name: updatedItem.record_user_name,
            };
        }

        const k = `${item.device_id}${item.gauge_id}${item.get_datetime}`;
        if (nextStatusKeySet.has(k)) {
            const ns = nextStatusByKey.get(k);
            return { ...item, status_class: ns?.status_class, status: ns?.status };
        }

        return item;
    });

    return {
        ...state,
        imageListData: updatedImageListData,
        gaugeData: nextMergedGaugeData,
    };
};


const dataManageReducer = (state = initialState, action) => {
    switch (action.type) {
        case ActionTypes.FETCH_FACILITIES_REQUEST:
        case ActionTypes.FETCH_IMAGE_LIST_REQUEST:
            return {
                ...state,
                isFetching: {
                    ...state.isFetching,
                    [action.type]: true,
                },
                isFetched: false,
            }
        case ActionTypes.FETCH_COUNT_REQUEST:
        case ActionTypes.FETCH_CAMERA_INFO_REQUEST:
        case ActionTypes.PHOTO_SHOOT_REQUEST:
        case ActionTypes.FETCH_PHOTO_SHOOT_STATUS_REQUEST:
        case ActionTypes.PHOTO_SHOOT_UPDATE_REQUEST:
        case ActionTypes.DATA_DELETE_REQUEST:
        case ActionTypes.DATA_EDIT_REQUEST:
        case ActionTypes.DATA_REGISTER_REQUEST:
            return {
                ...state,
                isFetching: {
                    ...state.isFetching,
                    [action.type]: true,
                },
            };
        case ActionTypes.FETCH_FACILITIES_SUCCESS:
            return {
                ...state,
                isFetching: {
                    ...state.isFetching,
                    [ActionTypes.FETCH_FACILITIES_REQUEST]: false,
                },
                facilities: action.payload.facility,
                cameras: action.payload.camera,
                gauges: action.payload.gauge,
                statuses: action.payload.status,
                error: null,
            };
        case ActionTypes.FETCH_IMAGE_LIST_SUCCESS:
            return {
                ...state,
                isFetching: {
                    ...state.isFetching,
                    [ActionTypes.FETCH_IMAGE_LIST_REQUEST]: false,
                },
                isFetched: true,
                imageListData: action.payload.data,
                gaugeData: action.payload.gaugeData,
                failure_id: action.payload.failure_id,
                new_start_date: action.payload.new_start_date || null,
                is_null: action.payload.data.length === 0,
                error: null,
            };
        case ActionTypes.FETCH_COUNT_SUCCESS:
            return {
                ...state,
                isFetching: {
                    ...state.isFetching,
                    [ActionTypes.FETCH_COUNT_REQUEST]: false,
                },
                searchCount: action.payload.count,
                searchKeys: action.payload.keys,
                error: null,
            };
        case ActionTypes.FETCH_CAMERA_INFO_SUCCESS:
            return {
                ...state,
                isFetching: {
                    ...state.isFetching,
                    [ActionTypes.FETCH_CAMERA_INFO_REQUEST]: false,
                },
                cameraInfo: action.payload,
                error: null,
            };
        case ActionTypes.CLEAR_CAMERA_INFO:    // Reset cameraInfo
            return {
                ...state,  
                cameraInfo: null,              
                error: null,
            };
        case ActionTypes.PHOTO_SHOOT_SUCCESS:    // 遠隔撮影    
            return {
                ...state,
                isFetching: {
                    ...state.isFetching,
                    [ActionTypes.PHOTO_SHOOT_REQUEST]: false,
                },
                cameraInfo: {
                    ...state.cameraInfo,
                    photoshoot_info: action.payload,
                },            
                error: null,
            };
        case ActionTypes.FETCH_PHOTO_SHOOT_STATUS_SUCCESS:    // 遠隔撮影ステータス取得
                return {
                    ...state,
                    isFetching: {
                        ...state.isFetching,
                        [ActionTypes.FETCH_PHOTO_SHOOT_STATUS_REQUEST]: false,
                    },
                    cameraInfo: {
                        ...state.cameraInfo,
                        photoshoot_info: {
                            ...state.cameraInfo?.photoshoot_info,
                            photoshoot_status: action.payload,     // Add or update photoshoot_status
                        },
                    },
                    error: null,
                };
        case ActionTypes.PHOTO_SHOOT_UPDATE_SUCCESS:    // 遠隔撮影画像データをシステムへ更新    
                return {
                    ...state,
                    isFetching: {
                        ...state.isFetching,
                        [ActionTypes.PHOTO_SHOOT_UPDATE_REQUEST]: false,
                    },
                    cameraInfo: {
                        ...state.cameraInfo,
                        photoshoot_info: {
                            ...state.cameraInfo?.photoshoot_info,
                            photoshoot_result: action.payload,     // Add or update photoshoot_result
                        },
                    },           
                    error: null,
                };
        case ActionTypes.DATA_REGISTER_SUCCESS:
            return {
                ...state,
                ...updateData(state, action.payload.respons),
                isFetching: {
                    ...state.isFetching,
                    [ActionTypes.DATA_REGISTER_REQUEST]: false,
                },
                error: null,
            };
        case ActionTypes.DATA_EDIT_SUCCESS:
            return {
                ...state,
                ...updateData(state, action.payload.respons),
                isFetching: {
                    ...state.isFetching,
                    [ActionTypes.DATA_EDIT_REQUEST]: false,
                },
                error: null,
            };
        case ActionTypes.DATA_DELETE_SUCCESS:
            return {
                ...state,
                ...clearData(state, action.payload.respons),
                isFetching: {
                    ...state.isFetching,
                    [ActionTypes.DATA_DELETE_REQUEST]: false,
                },
                error: null,
            };
        case ActionTypes.FETCH_FACILITIES_FAILURE:
        case ActionTypes.FETCH_IMAGE_LIST_FAILURE:
            return {
                ...state,
                isFetching: {
                    ...state.isFetching,
                    [action.type.replace('_FAILURE', '_REQUEST')]: false,
                },
                isFetched: false,
            }
        case ActionTypes.FETCH_COUNT_FAILURE:
        case ActionTypes.FETCH_CAMERA_INFO_FAILURE:
        case ActionTypes.PHOTO_SHOOT_FAILURE:
        case ActionTypes.FETCH_PHOTO_SHOOT_STATUS_FAILURE:
        case ActionTypes.PHOTO_SHOOT_UPDATE_FAILURE:
        case ActionTypes.DATA_REGISTER_FAILURE:
        case ActionTypes.DATA_EDIT_FAILURE:
        case ActionTypes.DATA_DELETE_FAILURE:
            return {
                ...state,
                isFetching: {
                    ...state.isFetching,
                    [action.type.replace('_FAILURE', '_REQUEST')]: false,
                },
                error: action.payload,
            };
        case ActionTypes.SET_SELECTED_ITEM:
            return {
                ...state,
                selectedItem: action.payload,
                relativeActive: null
            };
        case ActionTypes.SET_RELATIVE_ACTIVE:
            return {
                ...state,
                relativeActive: action.payload,
            };
        default:
            return state;
    }
};

export default dataManageReducer;
